import React, { useState } from 'react';
import { PopUp } from '@/components/design-system/molecules/pop-up';
import { TextInput } from '@/components/design-system/atoms/text/text-input';
import { OptionStateSelector } from '@/components/design-system/atoms/option-state-selector';
import { DropDown } from '@/components/design-system/molecules/dropdown';
import { DatePicker } from '@/components/design-system/molecules/datepicker';
import { RichTextEditor } from '@/components/design-system/molecules/rich-text-editor';
import { Modal } from '@/components/design-system/atoms/modal';
import type { DiscountFormData } from '../../types/discount';
import SubscriptionSuccessModal from '@/components/SubscriptionSuccessModal';
import FileUpload from '@/components/FileUpload';
import { useTranslation } from 'react-i18next';
import {
  periodOptions,
  typeOptions,
} from '@/features/admin/contracts/components/contracts/constants/discount';
import SuccessModal from '@/components/SuccessModal';

export type DiscountModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: DiscountFormData) => Promise<boolean | void>;
  contract: any;
};

const DiscountModal: React.FC<DiscountModalProps> = ({ open, onClose, onSubmit, contract }) => {
  const { t } = useTranslation();
  const [amount, setAmount] = useState<number | ''>('');
  const [amountError, setAmountError] = useState<string | undefined>(undefined);
  const [unit, setUnit] = useState('€');
  const [type, setType] = useState('');
  const [period, setPeriod] = useState('this_month');
  const [note, setNote] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [startDateError, setStartDateError] = useState<string | undefined>(undefined);
  const [endDateError, setEndDateError] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments((prev) => [...prev, ...Array.from(e.target.files!)]);
    }
  };

  const handleRemoveAttachment = (fileName: string) => {
    setAttachments((prev) => prev.filter((file) => file.name !== fileName));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (amount === '') {
      setAmountError(t('js.features.admin.contracts.discount_modal.form.amount.error') as string);
      return;
    }
    setAmountError(undefined);

    if (period === 'recurring') {
      if (!startDate) {
        setStartDateError(
          t('js.features.admin.contracts.discount_modal.form.start_date.error') as string
        );
        return;
      }
      if (!endDate) {
        setEndDateError(
          t('js.features.admin.contracts.discount_modal.form.end_date.error') as string
        );
        return;
      }
    }
    setStartDateError(undefined);
    setEndDateError(undefined);

    setIsLoading(true);
    // Call the API first
    const success = await onSubmit({
      amount: amount as number,
      unit: unit as '€' | '%',
      discount_category: Number(type),
      period: period as 'this_month' | 'next_month' | 'recurring',
      note,
      attachments,
      startDate,
      endDate,
    });
    setIsLoading(false);

    // Then show success modal
    if (success) {
      setShowSuccessModal(true);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose}>
      {showSuccessModal ? (
        <SuccessModal
          isOpen={showSuccessModal}
          title={t('js.features.admin.contracts.discount_modal.success.title') as string}
          description={
            t('js.features.admin.contracts.discount_modal.success.description') as string
          }
          onClose={() => {
            setShowSuccessModal(false);
            onClose();
          }}
        />
      ) : (
        <PopUp
          type="default"
          title={t('js.features.admin.contracts.discount_modal.title') as string}
          primaryButtonProps={{
            children: isLoading
              ? (t('js.features.admin.contracts.discount_modal.loading') as string)
              : (t('js.features.admin.contracts.discount_modal.add_button') as string),
            type: 'submit',
            onClick: handleSubmit,
            className: 'flex-1',
            disabled: isLoading,
          }}
          secondaryButtonProps={{
            children: t('js.features.admin.contracts.discount_modal.cancel_button') as string,
            type: 'button',
            onClick: onClose,
            className: 'flex-1',
            variant: 'ghost',
            disabled: isLoading,
          }}
          buttonOrder="primary-first"
        >
          <PopUp.PopUpInput>
            <div className="flex flex-col gap-4">
              <div className="flex gap-3">
                <TextInput
                  label={
                    t('js.features.admin.contracts.discount_modal.form.amount.label') as string
                  }
                  type="number"
                  step="0.01"
                  min="0"
                  value={amount === '' ? undefined : amount}
                  onChange={(e) => {
                    if (amountError) setAmountError(undefined);
                    setAmount(e.target.value === '' ? '' : Number(e.target.value));
                  }}
                  placeholder={
                    t(
                      'js.features.admin.contracts.discount_modal.form.amount.placeholder'
                    ) as string
                  }
                  required
                  className="flex-1"
                  invalid={!!amountError}
                  errorMessage={amountError}
                />
                <OptionStateSelector
                  options={[
                    { value: '€', label: '€' },
                    { value: '%', label: '%' },
                  ]}
                  selectedOption={unit}
                  onSelect={setUnit}
                  btnClassName="mt-[7px]"
                />
              </div>
              <div className="flex gap-2">
                <DropDown
                  label={
                    t('js.features.admin.contracts.discount_modal.form.category.label') as string
                  }
                  value={typeOptions.find((opt) => opt.value === Number(type))?.label ?? ''}
                  onChange={(value) => {
                    const selectedOption = typeOptions.find((opt) => opt.label === value);
                    setType(selectedOption?.value.toString() ?? '');
                  }}
                  placeholder={
                    t(
                      'js.features.admin.contracts.discount_modal.form.category.placeholder'
                    ) as string
                  }
                  className="flex-1 text-sm"
                  showLabel={true}
                >
                  <DropDown.DropDownOptions>
                    {typeOptions.map((opt) => (
                      <DropDown.DropDownOption
                        key={opt.value}
                        value={opt.label}
                        className="ml-2 mt-2 text-small"
                      >
                        {opt.label}
                      </DropDown.DropDownOption>
                    ))}
                  </DropDown.DropDownOptions>
                </DropDown>
                <DropDown
                  label={
                    t('js.features.admin.contracts.discount_modal.form.period.label') as string
                  }
                  value={periodOptions.find((opt) => opt.value === period)?.label ?? ''}
                  onChange={(value) => {
                    const selectedOption = periodOptions.find((opt) => opt.label === value);
                    setPeriod(selectedOption?.value ?? '');
                  }}
                  placeholder={
                    t(
                      'js.features.admin.contracts.discount_modal.form.period.placeholder'
                    ) as string
                  }
                  className="flex-1 text-sm"
                  showLabel={true}
                >
                  <DropDown.DropDownOptions>
                    {periodOptions.map((opt) => (
                      <DropDown.DropDownOption
                        key={opt.value}
                        value={opt.label}
                        className="mb-2 ml-2 text-small "
                      >
                        {opt.label}
                      </DropDown.DropDownOption>
                    ))}
                  </DropDown.DropDownOptions>
                </DropDown>
              </div>
              {period === 'recurring' && (
                <div className="flex gap-2">
                  <DatePicker
                    label={
                      t(
                        'js.features.admin.contracts.discount_modal.form.start_date.label'
                      ) as string
                    }
                    value={startDate}
                    onChange={(date: Date) => {
                      const adjustedDate = new Date(
                        date.getTime() - date.getTimezoneOffset() * 60000
                      );
                      setStartDate(adjustedDate);
                      setStartDateError(undefined);
                    }}
                    className="flex-1"
                    type="date"
                    invalid={!!startDateError}
                    errorMessage={startDateError}
                  />
                  <DatePicker
                    label={
                      t('js.features.admin.contracts.discount_modal.form.end_date.label') as string
                    }
                    value={endDate}
                    onChange={(date: Date) => {
                      const adjustedDate = new Date(
                        date.getTime() - date.getTimezoneOffset() * 60000
                      );
                      setEndDate(adjustedDate);
                      setEndDateError(undefined);
                    }}
                    className="flex-1"
                    type="date"
                    invalid={!!endDateError}
                    errorMessage={endDateError}
                  />
                </div>
              )}
              <RichTextEditor
                value={note}
                onChange={setNote}
                label={t('js.features.admin.contracts.discount_modal.form.note.label') as string}
                className="w-full"
                maxRichTextLength={200}
              />
              <FileUpload
                attachments={attachments}
                handleFileChange={handleFileChange}
                handleRemoveAttachment={handleRemoveAttachment}
              />
            </div>
          </PopUp.PopUpInput>
        </PopUp>
      )}
    </Modal>
  );
};

export default DiscountModal;
